import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import styles from './index.module.less'
import './styles.less';
import { Graph, Minimap, Transform, XFlow, Cell, Grid } from "@antv/xflow"
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels"
import { LeftPanel } from "./components/LeftPanel/LeftPanel"
import './nodes/registers'
import { EditorGraph, GridSize } from "./EditorGraph"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { FlowFileFooter } from "./components/Footer/Footer"
import { FlowsheetContent, NodeShapes, SheetContent } from "./services/modal"
import { cellsToSheetData, cacheImages, isLine, edgeToLineRecord, cellToNodeState } from "./services/storage"
import { FlowsheetContext } from "./FlowsheetProvider"
import { dataToSimFlow, removeTemplateCells } from "./utils"
import classNames from "classnames"
import { FlowHeader } from "./components/Header/Header"
import { SimulationModal, saveSimulation } from "@/services/project/simulation.service"
import { useReactState } from "./hooks/useReactState"
import { createBaseStream, createBasicLine, createImageNode, createTextNode } from "./nodes"
import { DrawingStream } from "./components/DrawingStream/DrawingStream"
import { tagGenerator } from "./services/TagGenerator"
import { FooterRight } from "./components/Footer/FooterRight"
import { FlowEvents } from "./components/FlowEvents/FlowEvents"
import { useDispatch, useSelector } from "react-redux"
import { runSimThunk, updateTopologyThunk } from "@/store/flowsheet/actions"
import { AppDispatch, store } from "@/store"
import { RunState, getFSContentSelector, getFlowSimulationSelector, getSetOperations, getStatus, saveCurrentSheet, setFSTempContent, setSheetId, updateSheetImages } from "@/store/flowsheet/flowsheet.slice"
import { EventMap, useEditorEventBusInstance } from "./events/useEventBusInstance"
import { FlowTemplate } from "./components/Template/Template"
import { BasicShape } from "./components/LeftPanel/BasicNode"
import { DrawingMode } from "./constant"
import { TempGraph } from "./TempGraph"
import { EditorContextMenu } from "./components/EditorContextMenu"
import { PanelType } from "./components/RightPanel/types"
import { SvgStreamPlugin } from "./plugins/SvgStreamPlugin"
import { EditorActions } from "./components/EditorActions/EditorActions"
import { DrawingLine } from "./components/DrawingLine/DrawingLine"
import { RightPanel } from "./components/RightPanel/RightPanel"
import { useCurrentSheet } from "./hooks/useCurrentSheet"
import { isEqual } from "lodash"
import { UnitSimUpdatePlugin } from "./plugins/UnitSimPlugin/UnitSimUpdatePlugin"
import { FLOWSHEET_VERSION } from "./migration"
import { usePrintFlowFile } from "./hooks/usePrintFlowFile"
import { OHaveAnyPermission, OPermission } from "../OPermission/OPermission"
import { ROLE_DRAFTER } from "../OPermission/PlanPermission"
import { usePrintContent } from "./services/print"

const miniMapOption = {
  width: 200,
  height: 160,
  padding: 10,
}

interface FlowSheetEditorProps {
  template?: FlowsheetContent
  isTemplate: boolean
  onSave?: (content: FlowsheetContent) => Promise<void>
}


export const FlowSheetEditor = memo((props:FlowSheetEditorProps) => {
  const { onSave, template, isTemplate} = props

  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const selectedState = useReactState<Cell|null>(null)
  const content = useSelector(getFSContentSelector)
  const selectedSheet = useCurrentSheet()
  const ref = useRef<{getGraph: () => Graph}>()
  const drawingMode = useReactState<DrawingMode>(DrawingMode.None)
  const hideTool = useReactState(false)
  const dispatch = useDispatch<AppDispatch>()
  const eventBus = useEditorEventBusInstance()
  const rightPanelState = useReactState<PanelType | null>(null)
  const status = useSelector(getStatus);
  const flowSimulation = useSelector(getFlowSimulationSelector)
  const simDataRef = useRef<any>(null)
  const printFile = usePrintFlowFile()
  const printContent = usePrintContent()

  useEffect(() => {
    const handler = () => {
      rightPanelState.setValue(PanelType.Template)
    }
    eventBus.on(EventMap.template.openSettingPanel, handler)

    return () => {
      eventBus.off(EventMap.template.openSettingPanel, handler)
    }
  }, [])

  const saveCurrent = useCallback(async () => {
    const graph = ref.current?.getGraph()
    if (!graph) return;
    const data = graph.toJSON()
    if (isTemplate) {
      const tempCells = removeTemplateCells(data.cells).map(item => {
        delete item.tools;
        return item
      })
      dispatch(setFSTempContent(tempCells))
    } else {
      const sheetData = await cellsToSheetData(data, graph)
      dispatch(saveCurrentSheet(sheetData))
    }
    const newContent = getFSContentSelector(store.getState())

    return newContent;
  }, [])

  useEffect(() => {
    selectedState.setValue(null)
  }, [selectedSheet.id])

  const context = useMemo(() => {
    const setDrawingSheet = async (id: string) => {
      if (id === selectedSheet.id) return
      await saveCurrent()
      dispatch(setSheetId(id))
    }

    const generateImages = async (contentData: FlowsheetContent) => {
      const images = await Promise.all(contentData.sheets.map((sheet) => {
        return printContent(sheet, template)
      }))
      dispatch(updateSheetImages(images))
      return getFSContentSelector(store.getState())
    }

    const createSaveContent = async (): Promise<FlowsheetContent | undefined> => {
      let contentData = await saveCurrent()
      if (!contentData) return
      contentData = await generateImages(contentData)

      const contentToSave = cacheImages(contentData)
      const setOperations = store.getState().flowsheet.setOperations
      const spreadsheets = store.getState().flowsheet.spreadsheets
      const template = store.getState().flowsheet.template
      contentToSave.setOperations = setOperations
      contentToSave.spreadsheets = spreadsheets
      contentToSave.template = template
      contentToSave.version = FLOWSHEET_VERSION
      return contentToSave;
    }
    
    const save = async () => {
      const tagOptions = tagGenerator.getTagState()
      const contentToSave = await createSaveContent()
      if (!contentToSave) return
      await onSave?.({...contentToSave, tagState: tagOptions})
      return contentToSave;
    }
    return {
      setSelectedSheet: setDrawingSheet,
      save: async () => {
        const res = await save();
        if (flowSimulation?.id) {
          await saveSimulation({sId: flowSimulation.id})
        }
        return res;
      },
      print: async () => {
        const res = await createSaveContent();
        const images = res?.sheets.map(sheet => ({image: sheet.image, name: sheet.name}))
        printFile((images || []) as any, template)
      },
      runWithSimlation: async (sim: SimulationModal) => {
        const data = await save()
        if (!data) return
        const simData = dataToSimFlow(data)
        dispatch(runSimThunk({data: simData, params: {sId: sim.id}}))
        simDataRef.current = simData;
      },
      updateTopology: async (sim: SimulationModal) => {
        const data = await save()
        if (!data) return
        const simData = dataToSimFlow(data)
        const oldSimData = simDataRef.current
        if (isEqual(oldSimData, simData)) {
          return;
        }
        await dispatch(updateTopologyThunk({data: simData, params: {sId: sim.id}})).unwrap()
        simDataRef.current = simData;
      },
      handleTopology: async (sim: SimulationModal, callback) => {
        const data = await save()
        if (!data) return
        const simData = dataToSimFlow(data)
        simDataRef.current = simData;
        return await callback(simData, {sId: sim.id})
      },
      changeCellProps: (id, props) => {
        const graph = ref.current?.getGraph()
        if (!graph) return;
        const cell = graph.getCellById(id)
        if (!cell) return;
        const data = cell.getData();
        cell.setData({...data, ...props}, {overwrite: true})
      },
      getAllUnits: async () => {
        const data = await save()
        if (!data) return {nodes: [] as any, lines: [] as any, operations: []}
        const operations = getSetOperations(store.getState())
        return {
          nodes: data.sheets.flatMap(item => item.nodes),
          lines: data.sheets.flatMap(item => item.lines),
          operations: operations,
        }
      },
      getCellById: (id: string) => {
        const graph = ref.current?.getGraph()
        if (!graph) return;
        const cell = graph.getCellById(id)
        if (!cell) return;
        if (isLine(cell)) {
          return edgeToLineRecord(cell as any)
        }
        return cellToNodeState(cell as any)
      },
      isTemplate,
      template
    }
  }, [selectedSheet, content, flowSimulation, saveCurrent, isTemplate, template, printContent])

  const changeSelectedSheet = (sheet: SheetContent) => {
    context.setSelectedSheet(sheet.id)
  }

  const onCellSelect = (params) => {
    const {node, edge} = params
    if (node) {
      if (node.shape === NodeShapes.UnitTag) {
        const graph = ref.current?.getGraph()
        const cell = graph?.getCellById(node.getData().unit)
        selectedState.setValue(cell || null)
        hideTool.setValue(true)
        
        return;
      } else if (node.shape === NodeShapes.SvgNode) {
        hideTool.setValue(false)
        return selectedState.setValue(node)
      } else if ([NodeShapes.TextNode, NodeShapes.ImageNode].includes(node.shape) && !isTemplate) {
        selectedState.setValue(node)
        rightPanelState.setValue(PanelType.Cell)
      } else {
        selectedState.setValue(node)
      }
      
    } else if (edge) {
      selectedState.setValue(edge)
    }
  }

  const onCellDelete = (cell: Cell) => {
    if (selectedState.value?.id === cell.id) {
      selectedState.setValue(null)
    }
  }

  const handleDragover = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = async (e: React.DragEvent) => {
    const graph = ref.current?.getGraph()
    const offsetX = Number(e.dataTransfer.getData('offsetX'))
    const offsetY = Number(e.dataTransfer.getData('offsetY'))
    const type = Number(e.dataTransfer.getData('type'))

    const startPoint = graph!.clientToLocal(e.clientX - offsetX, e.clientY - offsetY)
    const {x, y} = startPoint
    
    if (type === BasicShape.Line || type === BasicShape.Stream) {
      let edgeOptions;
      if (type === BasicShape.Line) {
        edgeOptions = createBasicLine(startPoint, {x: startPoint.x + 100, y: startPoint.y})
      } else {
        edgeOptions = createBaseStream(startPoint, {x: startPoint.x + 100, y: startPoint.y})
      }
      graph?.addEdge(edgeOptions)
    } else if (type === BasicShape.Text) {
      const textNodeOptions = createTextNode(x, y)
      graph?.addNode(textNodeOptions)
    } else if (type === BasicShape.Image) {
      const imageOptions = createImageNode(x, y)
      graph?.addNode(imageOptions)
    }
  }

  const onTopologyChange = () => {
    if (flowSimulation && status === RunState.Run) {
      context.updateTopology(flowSimulation)
    }
  }


  return <div className={classNames(styles.orbitEditor, drawingMode.value !== DrawingMode.None && 'drawing-stream', hideTool.value && 'hide-node-tool')}>
    <FlowsheetContext.Provider value={context}>
      <XFlow key={selectedSheet.id}>
        <FlowHeader isTemplate={isTemplate} sim={flowSimulation} panelState={rightPanelState} />
        <div className={styles.editorBody}>
          <PanelGroup direction="horizontal" style={{height: '100%'}}>
            <OPermission permissions={ROLE_DRAFTER}>
              <Panel defaultSize={20} minSize={15} className={styles.leftPanel} hidden={!showLeftPanel}>
                <LeftPanel isTemplate={isTemplate} />
              </Panel>
              <PanelResizeHandle style={{position: 'relative'}}>
                <div className={styles.leftTrigger} onClick={() => setShowLeftPanel(!showLeftPanel)}>
                  {showLeftPanel && <LeftOutlined style={{fontSize: 24}} />}
                  {!showLeftPanel && <RightOutlined style={{fontSize: 24}} />}
                </div>
              </PanelResizeHandle>
            </OPermission>
            
            <Panel defaultSize={60} style={{position: 'relative'}}>
                <div style={{width: '100%', height: '100%', cursor: 'grab'}} onDragOver={handleDragover} onDrop={handleDrop}>
                  {isTemplate && <TempGraph
                    content={content.tempCells!}
                    ref={ref}
                    onSelected={onCellSelect}
                    key={`${selectedSheet.id}_template_graph`}
                  />}
                  {!isTemplate && <EditorGraph
                    drawingStream={drawingMode.value}
                    template={template}
                    onRemove={onCellDelete}
                    onChange={onTopologyChange}
                    content={selectedSheet} ref={ref} key={`${selectedSheet.id}_graph`} onSelected={onCellSelect} />}
                </div>
                {/* <MeshGrid /> */}
                <Grid
                  type="doubleMesh"
                  size={GridSize}
                  options={[
                    {
                      color: '#bbbbbb', // 主网格线颜色
                      thickness: 1, // 主网格线宽度
                    },
                    {
                      color: '#666666', // 次网格线颜色
                      thickness: 1, // 次网格线宽度
                      factor: 4, // 主次网格线间隔
                    },
                  ]}
                />
                <Minimap key={`${selectedSheet.id}_minimap`} {...miniMapOption} style={{ position: 'absolute', right: 20, bottom: 60, border: '1px solid #bbb' }} />
                <Transform resizing rotating key={`${selectedSheet.id}_transform`} />
                {/* <OSnaplinePlugin key={`${selectedSheet.id}_snapline`} tolerance={10} /> */}
                {drawingMode.value === DrawingMode.Stream && <DrawingStream />}
                {drawingMode.value === DrawingMode.Line && <DrawingLine />}
                <FlowTemplate />
                <SvgStreamPlugin content={content} selected={selectedSheet} onContentChange={(newContent) => {
                  // setContent(newContent)
                }} />
            </Panel>
            <OHaveAnyPermission permissions={['DRAFTER', 'CHEMENG']}>
              <PanelResizeHandle className="w-2" disabled={!rightPanelState.value} />
              <div className={styles.iconActions}>
                <EditorActions
                  rightPanelState={rightPanelState}
                  drawingMode={drawingMode}
                  onTopologyChange={onTopologyChange}
                  isTemplate={isTemplate}
                />
              </div>
              {rightPanelState.value && <Panel collapsible={false} defaultSize={40} minSize={30} className={classNames(styles.rightPanel, rightPanelState.value && styles.closeRightStatus)}>
                <RightPanel cell={selectedState.value} type={rightPanelState.value} onChange={onTopologyChange} />
              </Panel>}
            </OHaveAnyPermission>
            
          </PanelGroup>
        </div>
        <div className={styles.footer}>
          {isTemplate ? <div></div> : <FlowFileFooter sheets={content.sheets} onAddSheet={changeSelectedSheet} />}
          <FooterRight graph={ref.current?.getGraph()} />
        </div>
        <FlowEvents />
        <EditorContextMenu />
        <UnitSimUpdatePlugin />
      </XFlow>
    </FlowsheetContext.Provider>

  </div>
})